import {useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {isInValidEmailFormat} from '../../../shared/util/EmailUtil';
import type {ProjectOption} from '../ClientContactModal';

const MAX_STANDARD = 191;
const MAX_PHONE = 45;

export type ClientContactFormValues = {
	firstName: string;
	lastName: string;
	email: string;
	jobTitle: string;
	phone: string;
	projectIds: string[];
};

export type UseClientContactFormReturn = {
	firstName: string;
	handleFirstNameChange: (val: string) => void;
	firstNameError: string;
	lastName: string;
	handleLastNameChange: (val: string) => void;
	lastNameError: string;
	email: string;
	handleEmailChange: (val: string) => void;
	emailError: string;
	setEmailError: (msg: string) => void;
	handleEmailBlur: () => void;
	jobTitle: string;
	handleJobTitleChange: (val: string) => void;
	phone: string;
	handlePhoneChange: (val: string) => void;
	handleGuestUserToggle: () => void;
	selectedProjectKeys: string[];
	availableProjects: ProjectOption[];
	selectedProjects: ProjectOption[];
	handleAddProject: (projectId: string) => void;
	handleRemoveProject: (projectId: string) => void;
	resetProjects: () => void;
	validateRequired: () => boolean;
	handleSubmit: () => void;
};

export type EmailUniqueValidator = (email: string) => Promise<boolean>;

export const useClientContactForm = (
	projectOptions: ProjectOption[],
	isGuestUser: boolean,
	onGuestUserToggle: () => void,
	onSubmit?: (values: ClientContactFormValues) => void,
	validateEmailUnique?: EmailUniqueValidator,
	initialValues?: Partial<ClientContactFormValues>
): UseClientContactFormReturn => {
	const {formatMessage} = useIntl();

	const [firstName, setFirstName] = useState(initialValues?.firstName ?? '');
	const [firstNameError, setFirstNameError] = useState('');
	const [lastName, setLastName] = useState(initialValues?.lastName ?? '');
	const [lastNameError, setLastNameError] = useState('');
	const [email, setEmail] = useState(initialValues?.email ?? '');
	const [emailError, setEmailError] = useState('');
	const [jobTitle, setJobTitle] = useState(initialValues?.jobTitle ?? '');
	const [phone, setPhone] = useState(initialValues?.phone ?? '');
	const [selectedProjectKeys, setSelectedProjectKeys] = useState<string[]>(initialValues?.projectIds ?? []);

	const lastUniqueCheckRef = useRef(0);

	const handleFirstNameChange = (val: string) => {
		setFirstName(val.slice(0, MAX_STANDARD));
		if (firstNameError) setFirstNameError('');
	};

	const handleLastNameChange = (val: string) => {
		setLastName(val.slice(0, MAX_STANDARD));
		if (lastNameError) setLastNameError('');
	};

	const handleEmailChange = (val: string) => {
		setEmail(val.slice(0, MAX_STANDARD));
		if (emailError) setEmailError('');
		// Cancel any in-flight uniqueness check — its result is stale.
		lastUniqueCheckRef.current++;
	};

	const handleEmailBlur = async (): Promise<boolean> => {
		const trimmed = email.trim();
		if (!trimmed) {
			setEmailError('');
			return true;
		}
		if (!isInValidEmailFormat(trimmed)) {
			setEmailError(formatMessage({id: 'client_contact.error.invalid_email'}));
			return false;
		}
		setEmailError('');
		if (!validateEmailUnique) return true;
		const checkId = ++lastUniqueCheckRef.current;
		try {
			const isUnique = await validateEmailUnique(trimmed);
			if (checkId !== lastUniqueCheckRef.current) return false;
			if (!isUnique) {
				setEmailError(formatMessage({id: 'client_contact.error.duplicate_email'}));
				return false;
			}
			return true;
		} catch {
			// Swallow check errors — submit will surface real failures via the error snackbar.
			return true;
		}
	};

	const handleJobTitleChange = (val: string) => setJobTitle(val.slice(0, MAX_STANDARD));
	const handlePhoneChange = (val: string) => setPhone(val.slice(0, MAX_PHONE));

	const resetProjects = () => setSelectedProjectKeys([]);

	const handleGuestUserToggle = () => {
		if (isGuestUser) resetProjects();
		onGuestUserToggle();
	};

	const handleAddProject = (projectId: string) => {
		if (!projectId) return;
		setSelectedProjectKeys(prev => (prev.includes(projectId) ? prev : [...prev, projectId]));
	};

	const handleRemoveProject = (projectId: string) => {
		setSelectedProjectKeys(prev => prev.filter(k => k !== projectId));
	};

	const availableProjects = projectOptions.filter(p => !selectedProjectKeys.includes(p.id));
	const selectedProjects = projectOptions.filter(p => selectedProjectKeys.includes(p.id));

	const validateRequired = (): boolean => {
		const trimmedEmail = email.trim();
		const fnErr = firstName.trim() === '' ? formatMessage({id: 'client_contact.error.first_name_required'}) : '';
		const lnErr = lastName.trim() === '' ? formatMessage({id: 'client_contact.error.last_name_required'}) : '';
		const emErr =
			trimmedEmail === ''
				? formatMessage({id: 'client_contact.error.email_required'})
				: !isInValidEmailFormat(trimmedEmail)
				? formatMessage({id: 'client_contact.error.invalid_email'})
				: '';

		setFirstNameError(fnErr);
		setLastNameError(lnErr);
		setEmailError(emErr);
		return !fnErr && !lnErr && !emErr;
	};

	const handleSubmit = async () => {
		if (!validateRequired()) return;

		const isEmailValid = await handleEmailBlur();
		if (!isEmailValid) return;

		onSubmit?.({
			firstName: firstName.trim(),
			lastName: lastName.trim(),
			email: email.trim(),
			jobTitle: jobTitle.trim(),
			phone: phone.trim(),
			projectIds: selectedProjectKeys,
		});
	};

	return {
		firstName,
		handleFirstNameChange,
		firstNameError,
		lastName,
		handleLastNameChange,
		lastNameError,
		email,
		handleEmailChange,
		emailError,
		setEmailError,
		handleEmailBlur,
		jobTitle,
		handleJobTitleChange,
		phone,
		handlePhoneChange,
		handleGuestUserToggle,
		selectedProjectKeys,
		availableProjects,
		selectedProjects,
		handleAddProject,
		handleRemoveProject,
		resetProjects,
		validateRequired,
		handleSubmit,
	};
};
